// src/components/legal/LegalDocumentRenderer.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// See IP License Agreement for usage restrictions.

import React from "react";

function renderBlock(block, index) {
  const key = `${block.type || "block"}-${index}`;

  if (block.type === "h3") {
    return <h3 className="legal-document__subheading" key={key}>{block.text}</h3>;
  }

  if (block.type === "ul" || block.type === "ol") {
    const List = block.type === "ol" ? "ol" : "ul";
    return (
      <List className="legal-document__list" key={key}>
        {(block.items || []).map((item) => <li key={item}>{item}</li>)}
      </List>
    );
  }

  if (block.type === "notice") {
    return <p className="legal-document__notice" key={key}>{block.text}</p>;
  }

  if (block.type === "contact") {
    return (
      <address className="legal-document__contact" key={key}>
        {(block.lines || []).map((line) => {
          const emailMatch = String(line).match(/^([^:]+:\s*)?([^\s@]+@[^\s@]+)$/);
          if (!emailMatch) return <span key={line}>{line}</span>;

          const prefix = emailMatch[1] || "";
          const email = emailMatch[2];
          return (
            <span key={line}>
              {prefix}
              <a href={`mailto:${email}`}>{email}</a>
            </span>
          );
        })}
      </address>
    );
  }

  return <p className="legal-document__paragraph" key={key}>{block.text}</p>;
}

export default function LegalDocumentRenderer({ document }) {
  if (!document) return null;

  return (
    <article className="legal-document">
      <div className="legal-document__metadata" aria-label="Document metadata">
        <span>Version {document.version}</span>
        <span aria-hidden="true">•</span>
        <span>Effective {document.effectiveDate}</span>
        <span aria-hidden="true">•</span>
        <span>Last updated {document.lastUpdated}</span>
      </div>

      <div className="legal-document__intro">
        {(document.intro || []).map((paragraph) => (
          <p className="legal-document__paragraph" key={paragraph}>{paragraph}</p>
        ))}
      </div>

      {(document.sections || []).map((section) => (
        <section className="legal-document__section" key={section.title}>
          <h2 className="legal-document__section-title">{section.title}</h2>
          <div className="legal-document__section-body">
            {(section.blocks || []).map(renderBlock)}
          </div>
        </section>
      ))}
    </article>
  );
}
